import { DraftLanguage, SocialCampaignDTO, StoryStatus } from 'models/socialCampaign'
import { SocialCampaignTarget, isSocialCampaignConflict, useCreateSocialCampaign } from 'dataQueries/socialCampaign.query'
import { SocialCampaignTrackingCampaign, failureOutcomeOf, httpStatusOf, trackSocialCampaignEvent } from 'utils/analytics/socialCampaign'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { GENERATION_STEPS } from './CampaignGenerating.component'
import { getBrowserTimezone } from 'utils/time/timezoneUtils'

/** How long each step stays in progress at least (the prototype's 550 ms read as a flicker on a real page). */
export const GENERATION_STEP_MS = 700

/**
 * How long the last step waits for the campaign's story at most (never stuck): past this the draft shows,
 * telling the template story until the backend rewrites it with the story (or falls back after 15 minutes).
 */
export const STORY_WAIT_MS = 90_000

/** How often the campaign is re-read while its story is written. */
export const STORY_POLL_MS = 3_000

/** The posts are written in German for a German interface, in English otherwise (EN and DE only). */
export const draftLanguageFor = (uiLanguage: string) =>
  uiLanguage.toLowerCase().startsWith('de') ? DraftLanguage.DE : DraftLanguage.EN

const elapsedMs = (since: number) => Math.round(performance.now() - since)

/** The campaign an answer holds, for the analytics; none when it holds no campaign. */
const trackedCampaignOf = (data: unknown): SocialCampaignTrackingCampaign => {
  const campaign = data as Partial<SocialCampaignDTO> | null | undefined
  return campaign?.id && campaign.phase ? { id: campaign.id, phase: campaign.phase } : null
}

interface Options {
  /** UI language, which picks the language of the posts */
  language: string
  /** Status of the campaign's story (ISSUE-3985) as the campaign query has it; none without ISSUE-3985 */
  storyStatus: StoryStatus | null | undefined
  /** Re-reads the campaign: a conflict means the property already has one; answers what it read */
  refetchCampaign: () => Promise<{ data?: unknown }>
  /**
   * Re-reads the campaign while its story is written, joining a read already on its way instead of
   * cancelling it: a read slower than `STORY_POLL_MS` must still land
   */
  pollCampaign: () => Promise<unknown>
  /** The campaign is there and every step was shown */
  onDone: () => void
}

/**
 * Starting a campaign (prototype v3 `start`): the creation request runs while the five steps follow
 * one another, each shown for at least `GENERATION_STEP_MS`; the last one waits for the campaign, then
 * for its story while the language model writes it (ISSUE-3985), re-reading the campaign meanwhile.
 * Never stuck: the request has a timeout (`CREATE_SOCIAL_CAMPAIGN_TIMEOUT_MS`), the story wait one
 * (`STORY_WAIT_MS`), a failure goes back to the intro with its error, and a conflict (the campaign was
 * created meanwhile, in another tab or by a request that timed out on its way back) shows that campaign.
 */
export function useCampaignGeneration(target: SocialCampaignTarget, { language, storyStatus, refetchCampaign, pollCampaign, onDone }: Options) {
  const { mutate } = useCreateSocialCampaign()
  /** Index of the step in progress; null when not generating */
  const [step, setStep] = useState<number | null>(null)
  const [created, setCreated] = useState(false)
  const [failed, setFailed] = useState(false)
  const [storyWaitOver, setStoryWaitOver] = useState(false)
  /** When the last step started waiting for the story; the wait's cap counts from there */
  const storyWaitSince = useRef<number | null>(null)
  /** A ref, not `step`: a double click lands before the first click's render */
  const isRunning = useRef(false)
  // what the generation's analytics tell once it is over (ISSUE-3637); `telling` until its finished event went out
  const startedAt = useRef(0)
  const telling = useRef(false)
  const outcome = useRef<{ kind: 'created' | 'existing', campaign: SocialCampaignTrackingCampaign, createMs?: number }>({ kind: 'created', campaign: null })
  const latestStoryStatus = useRef(storyStatus)
  latestStoryStatus.current = storyStatus

  // The client left before the draft showed (another tab of the property, the page closed): told as abandoned, with
  // the campaign when the creation had already answered
  const targetRef = useRef(target)
  targetRef.current = target
  useEffect(() => () => {
    if (!telling.current) return
    telling.current = false
    trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_GENERATION_FINISHED, targetRef.current, outcome.current.campaign, {
      outcome: 'abandoned',
      durationMs: elapsedMs(startedAt.current),
      createMs: outcome.current.createMs,
    })
  }, [])

  const waitingForStory = created && storyStatus === StoryStatus.GENERATING && !storyWaitOver

  const start = useCallback(() => {
    if (isRunning.current) return
    isRunning.current = true
    const draftLanguage = draftLanguageFor(language)
    trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_GENERATION_STARTED, target, null, { draftLanguage, retry: failed })
    startedAt.current = performance.now()
    telling.current = true
    outcome.current = { kind: 'created', campaign: null }
    setFailed(false)
    setCreated(false)
    setStoryWaitOver(false)
    storyWaitSince.current = null
    setStep(0)
    mutate(
      { ...target, body: { timezone: getBrowserTimezone(), draftLanguage } },
      {
        onSuccess: (answer) => {
          outcome.current = { kind: 'created', campaign: trackedCampaignOf(answer), createMs: elapsedMs(startedAt.current) }
          setCreated(true)
        },
        onError: async (error) => {
          const createMs = elapsedMs(startedAt.current)
          // the property already has a campaign: show it, unless it cannot be read either
          const existing = isSocialCampaignConflict(error) ? (await refetchCampaign()).data : undefined
          if (existing) {
            outcome.current = { kind: 'existing', campaign: trackedCampaignOf(existing), createMs }
            setCreated(true)
            return
          }
          isRunning.current = false
          // gone meanwhile: the unmount told it as abandoned
          if (!telling.current) return
          telling.current = false
          trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_GENERATION_FINISHED, target, null, {
            outcome: 'failed',
            durationMs: elapsedMs(startedAt.current),
            createMs,
            failure: failureOutcomeOf(error),
            httpStatus: httpStatusOf(error),
          })
          setStep(null)
          setFailed(true)
        },
      }
    )
  }, [failed, language, mutate, refetchCampaign, target])

  useEffect(() => {
    if (step === null) return
    const last = GENERATION_STEPS.length - 1
    // the last step stays in progress until the campaign is there, then while its story is written
    if (step >= last && !created) return
    if (step >= last && waitingForStory) {
      storyWaitSince.current ??= Date.now()
      const poll = setInterval(() => void pollCampaign(), STORY_POLL_MS)
      const giveUp = setTimeout(() => setStoryWaitOver(true), STORY_WAIT_MS - (Date.now() - storyWaitSince.current))
      return () => {
        clearInterval(poll)
        clearTimeout(giveUp)
      }
    }

    const timer = setTimeout(() => {
      if (step < last) {
        setStep(step + 1)
        return
      }
      isRunning.current = false
      telling.current = false
      trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_GENERATION_FINISHED, target, outcome.current.campaign, {
        outcome: outcome.current.kind,
        durationMs: elapsedMs(startedAt.current),
        createMs: outcome.current.createMs,
        storyStatus: latestStoryStatus.current ?? 'NONE',
        storyWaitTimedOut: storyWaitOver,
      })
      setStep(null)
      setCreated(false)
      onDone()
    }, GENERATION_STEP_MS)

    return () => clearTimeout(timer)
  }, [created, onDone, pollCampaign, step, storyWaitOver, target, waitingForStory])

  return { step, failed, start }
}
